import { FormInput, InfoBox } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { getDefaultCurrency } from 'store/state';
import { lazy, number, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import { II18nextPrefix } from 'types/components/components.interface';

export interface IEventCashFlowBase {
  tax: number | null;
  amountGross: number | null;
  amountNet: number | null;
}

export interface IEventCashFlowForm extends IEventCashFlowBase {
  eventId: string;
  name: string;
  fileUrl: string | null;
  file?: File;
}

const validationSchema = object({}).shape({
  name: string().required(() => i18next.t('formik-error.required')),
  tax: number().required(() => i18next.t('formik-error.required')),
  amountNet: lazy((_, options) =>
    number().when('amountGross', {
      is: (gross: number | null) => gross === null,
      then: schema =>
        schema
          .required(() => i18next.t('formik-error.required'))
          .positive(() => i18next.t('formik-error.amount-must-be-positive')),
      otherwise: schema => schema.nullable(),
    })
  ),
  amountGross: lazy((_, options) =>
    number().when('amountNet', {
      is: (net: number | null) => net === null,
      then: schema =>
        schema
          .required(() => i18next.t('formik-error.required'))
          .positive(() => i18next.t('formik-error.amount-must-be-positive')),
      otherwise: schema => schema.nullable(),
    })
  ),
});

export const eventCashFlowInitialValues: IEventCashFlowForm = {
  eventId: '',
  amountGross: null,
  amountNet: null,
  tax: null,
  name: '',
  fileUrl: '',
};

interface IEventCashFlowFormProps extends FormikConfig<IEventCashFlowForm>, IFormProps, II18nextPrefix {}

const EventCashFlowForm = ({
  customBackItem,
  status,
  tittle,
  backPath,
  topbarContent,
  initialValues,
  i18nextPrefix,
  ...props
}: IEventCashFlowFormProps) => {
  const { t } = useTranslation();

  const defaultCurrency = getDefaultCurrency()?.currencyCode ?? '';

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      {...props}>
      {({ values }: IFormikProps<IEventCashFlowForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'event'}
              topbarContent={
                <div className='flex w-full items-center justify-end'>
                  <div className='flex w-full'>
                    {status?.displayMode ? null : (
                      <div className='w-full max-w-60'>
                        <FormInput.Text
                          name='name'
                          label={t('input.event.name')}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                  <div>{topbarContent}</div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={2}>
                  <FormInput.Number
                    name='amountNet'
                    label={t('input.event.net')}
                    placeholder={`0 ${defaultCurrency}`}
                    suffix={` ${defaultCurrency}`}
                  />
                  <FormInput.Number
                    name='amountGross'
                    label={t('input.event.gross')}
                    placeholder={`0 ${defaultCurrency}`}
                    suffix={` ${defaultCurrency}`}
                  />
                  <FormInput.Number
                    name='tax'
                    label={t('input.event.tax')}
                    placeholder='0 %'
                    suffix=' %'
                  />
                </InfoBox.Items>
              </InfoBox>
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

export default EventCashFlowForm; 